import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";

// Feedback triage for admins.
//
// The counterpart to FeedbackModal. Without it the table is a dead letter box:
// people would write in and nothing would ever read it, which is worse than
// having no form at all.
//
// Rows carry the sender's account, the build they were on and their platform,
// so a report can be acted on without a round of questions first.
const STATUSES = ["new", "read", "done"];

const TONE = {
  new: { bg: "#EFF8FF", text: "#0058A0" },
  read: { bg: "#FFF7E6", text: "#8A5A00" },
  done: { bg: "#EAF7EE", text: "#1B873F" },
};

const KIND_LABEL = { bug: "Bug", idea: "Idea", other: "Other" };

function AdminFeedbackPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("new");

  const load = useCallback(async () => {
    setLoading(true);
    // RLS restricts this to admins, so no filter here can be trusted to do it
    // and none is added.
    const { data } = await supabase
      .from("feedback")
      .select("id, user_id, kind, message, app_version, platform, status, created_at, users(first_name, last_name, email)")
      .order("created_at", { ascending: false })
      .limit(200);
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    const { error } = await supabase.from("feedback").update({ status }).eq("id", id);
    if (error) {
      console.error("Could not update feedback status:", error);
      return;
    }
    setRows((current) => current.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <div style={{ marginBottom: "2rem", padding: "1rem", border: "1px solid #ddd", borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>Feedback</h3>
        <div style={{ display: "flex", gap: 6 }}>
          {["new", "read", "done", "all"].map((value) => (
            <button
              key={value}
              onClick={() => setFilter(value)}
              style={{
                padding: "3px 10px",
                borderRadius: 999,
                border: "1px solid #ddd",
                backgroundColor: filter === value ? "#0072BC" : "#fff",
                color: filter === value ? "#fff" : "#374151",
                cursor: "pointer",
                fontSize: 12,
                textTransform: "capitalize",
              }}
            >
              {value}
              {value !== "all" && ` (${rows.filter((r) => r.status === value).length})`}
            </button>
          ))}
        </div>
        <button onClick={load} style={{ marginLeft: "auto", cursor: "pointer" }}>Refresh</button>
      </div>

      {loading && <p style={{ color: "#6B7280" }}>Loading…</p>}
      {!loading && shown.length === 0 && (
        <p style={{ color: "#6B7280" }}>Nothing here.</p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
        {shown.map((row) => {
          const tone = TONE[row.status] || TONE.new;
          const name = [row.users?.first_name, row.users?.last_name].filter(Boolean).join(" ");
          return (
            <div key={row.id} style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{KIND_LABEL[row.kind] || row.kind}</span>
                <span
                  style={{
                    fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                    backgroundColor: tone.bg, color: tone.text,
                    padding: "2px 8px", borderRadius: 999,
                  }}
                >
                  {row.status}
                </span>
                <span style={{ fontSize: 12, color: "#6B7280" }}>
                  {name || "Unknown"}{row.users?.email ? ` · ${row.users.email}` : ""}
                </span>
                <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: "auto" }}>
                  {new Date(row.created_at).toLocaleString()}
                </span>
              </div>

              <p style={{ fontSize: 14, color: "#111827", margin: "0 0 8px", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {row.message}
              </p>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                  v{row.app_version || "?"} · {row.platform || "?"}
                </span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  {STATUSES.filter((s) => s !== row.status).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(row.id, s)}
                      style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, border: "1px solid #ddd", backgroundColor: "#fff", cursor: "pointer", textTransform: "capitalize" }}
                    >
                      Mark {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AdminFeedbackPanel;
